import { useCallback, useState, type ReactNode } from 'react';
import ConnectedTopUpModal from '../components/TopUpModal/TopUpModal.connected';
import { useWalletOutcome } from './useWalletOutcome';
import type { tTopUpKind } from '../components/TopUpModal/TopUpModal.types';
import type { tTopUpOutcome } from '../Wallet.types';
import type { tWalletShortfall } from '../Wallet.types';

/**
 * Dueño del modal de recarga: su estado, con qué pestaña abre, y el modal ya
 * montado.
 *
 * POR QUÉ EXISTE. El modal se monta en tres pantallas —la pestaña de Saldo, la
 * sección General de WhatsApp API y el bloque de Ajustes— y cada una repetía las
 * mismas cuatro piezas: el booleano de visible, la pestaña inicial, el abridor que
 * fija las dos, y el `<ConnectedTopUpModal>` con su `initialKind`. Sólo funcionan
 * juntas y nada obligaba a copiarlas completas.
 *
 * Eso no es teoría: al añadir la pestaña inicial se cableó UNA pantalla y las otras
 * dos se quedaron con tres de las cuatro piezas. Compiló, cada pantalla siguió
 * pasando sus tests, y el switch de recarga automática abría en "única" en dos de
 * los tres sitios. El defecto no fue de lógica sino de olvido, y la duplicación es
 * lo que lo hacía posible.
 *
 * Con el hook no hay nada que olvidar: `initialKind` deja de ser un parámetro que
 * alguien pasa y pasa a ser algo que la puerta elegida ya decidió. Por eso también
 * desaparece el test que vigilaba que las tres pantallas lo pasaran — un problema
 * que no puede ocurrir no necesita vigilancia.
 *
 * NO se mete el modal dentro de BalanceCard, que sería la otra forma de unificarlo:
 * en la pestaña de Saldo el aviso de saldo bajo también lo abre, y vive fuera de la
 * tarjeta. El modal tiene que seguir siendo montable desde la pantalla.
 */
export function useTopUpModal(
  options: {
    onClose?: () => void;
    /**
     * QUÉ PASÓ en el modal, para quien lo montó (RET-1928).
     *
     * Va por el hook y no por cada pantalla a propósito, y es la misma razón por
     * la que existe `initialKind`: cuando el modal se montaba a mano en cada
     * superficie, cablear un aviso nuevo eran tres ediciones y bastaba olvidar
     * una para que una pantalla quedara muda sin que nada fallara. Aquí es una.
     *
     * Distinto de `onClose`: éste sólo corre cuando ALGO ocurrió. Cerrar la
     * ventana sin hacer nada no lo dispara, y esa es toda la distinción entre
     * arrepentirse y haber hecho algo — no hace falta un desenlace centinela que
     * cada consumidor tenga que acordarse de filtrar.
     *
     * Empezó siendo `onCharged`, atado al cobro (RET-1870). Se generalizó porque
     * un gancho POR desenlace no escala: con el alta de la recarga automática y
     * el alta de tarjeta serían tres ganchos ad hoc en esta firma, y la regla de
     * qué releer volvería a repartirse entre las pantallas. Lo que cada
     * desenlace arrastra vive en `Wallet.outcome.ts`, en un solo lugar.
     *
     * OPCIONAL PARA PERSONALIZARLO, NUNCA PARA QUEDARSE SIN ÉL. Omitirlo cae al
     * `useWalletOutcome()` de abajo, y ese default es una corrección de la
     * revisión de RET-1925 — antes omitirlo dejaba la superficie muda.
     *
     * POR QUÉ CAMBIÓ. Este hook se montaba en CINCO lugares (RET-2429 suma el
     * panel de costo de la campaña, también SIN opciones): la pestaña de Saldo,
     * la sección General de WhatsApp API, el bloque de Suscripción, y las dos
     * superficies del compositor de chat (`Template` y
     * `WabTemplatesVariablesAction`, RET-1806). Las dos del compositor llaman a
     * este hook SIN opciones. El invariante escrito aquí decía que eso no costaba
     * nada porque no pintan `WalletContext.balance`, y era cierto mientras el
     * desenlace sólo sirviera para repintar el saldo.
     *
     * RET-1925 lo volvió falso: el desenlace ahora arma también el candado que
     * frena una segunda recarga del mismo monto, y eso no tiene NADA que ver con
     * pintar el saldo. Peor todavía, el compositor es la superficie a la que se
     * llega PORQUE un envío se bloqueó por saldo insuficiente — el cliente con
     * más razón para repetir el cobro de inmediato. Ahí la omisión no perdía un
     * repintado: dejaba abierto el doble cobro.
     *
     * El default vive aquí y no en cada superficie por la misma razón que
     * `initialKind`: una superficie nueva hereda la conducta sin cablear nada, y
     * no hay nada que olvidar.
     */
    onOutcome?: (outcome: tTopUpOutcome) => void;
  } = {},
) {
  const { onClose, onOutcome } = options;
  /**
   * El desenlace por defecto. Se llama SIEMPRE, tenga o no `onOutcome` propio:
   * es un hook y su orden no puede depender de una opción.
   */
  const defaultOnOutcome = useWalletOutcome();
  const [isOpen, setIsOpen] = useState(false);
  const [initialKind, setInitialKind] = useState<tTopUpKind>('one-off');
  /**
   * El monto EXACTO que falta cubrir, en micro-unidades (RET-1806, DS-03).
   * `null` es el caso de siempre: se abre sin nada que prellenar. El redondeo
   * entero y el piso de RC-10 se aplican en `TopUpModal.connected.tsx`, no
   * aquí — este hook solo transporta el dato crudo hasta el punto donde se
   * conoce `minFreeAmountMicros`.
   */
  const [initialShortfall, setInitialShortfall] = useState<tWalletShortfall | null>(null);

  const open = useCallback(
    (kind: tTopUpKind, shortfall: tWalletShortfall | null = null) => {
      setInitialKind(kind);
      setInitialShortfall(shortfall);
      setIsOpen(true);
    },
    [],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    // Cada pantalla refresca lo suyo después de una recarga: la de Saldo vuelve a
    // la primera página de movimientos, las otras sólo releen el saldo. El cierre
    // en sí es del hook; lo que hay que refrescar, de quien lo monta.
    onClose?.();
  }, [onClose]);

  return {
    /** "Recargar": una recarga única. */
    openOneOff: useCallback(() => open('one-off'), [open]),
    /** El switch de recarga automática: abre en la pestaña de automática. */
    openRecurring: useCallback(() => open('recurring'), [open]),
    /**
     * Abre Recargar con la de saldo bajo preseleccionada (RET-2425, R20).
     *
     * Solo PIDE `'threshold'`. Qué tipo se abre de verdad lo decide el
     * contenedor, porque el hook no conoce el resultado del `GET` de la
     * configuración ni el flag.
     */
    openThreshold: useCallback(() => open('threshold'), [open]),
    /**
     * Recarga única con el monto libre PRELLENADO para cubrir un envío
     * (RET-1806, DS-03: el CTA "Recargar" del compositor sin saldo). Recibe
     * el faltante EXACTO en micro-unidades — nunca redondeado por el
     * llamador, ese paso es responsabilidad del modal (RC-10).
     */
    openOneOffWithAmount: useCallback(
      (shortfall: tWalletShortfall) => open('one-off', shortfall),
      [open],
    ),
    /** El modal, ya montado y con su pestaña. Se pinta tal cual. */
    modal: (
      isOpen ? (
        <ConnectedTopUpModal
          isOpen
          initialKind={initialKind}
          initialShortfall={initialShortfall}
          onClose={close}
          onOutcome={onOutcome ?? defaultOnOutcome}
        />
      ) : null
    ) as ReactNode,
  };
}
